'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface UtilitiesSectionProps {
  currentLanguage: string;
}

const UtilitiesSection = ({ currentLanguage }: UtilitiesSectionProps) => {
  const [activeTab, setActiveTab] = useState(0);

  const content = {
    en: {
      title: 'Utilities Management & Smart Grid',
      subtitle: 'Optimize energy distribution with AI-powered monitoring',
      tabs: [
        {
          name: 'Smart Grid',
          icon: 'BoltIcon',
          features: [
            'Real-time grid balancing',
            'Demand response optimization',
            'Renewable integration',
            'Peak load management'
          ]
        },
        {
          name: 'Energy Monitoring',
          icon: 'ChartBarIcon',
          features: [
            'IoT sensor networks',
            'Predictive analytics',
            'Consumption forecasting',
            'Anomaly detection'
          ]
        },
        {
          name: 'Sustainability',
          icon: 'SparklesIcon',
          features: [
            'Carbon tracking',
            'Emission reporting',
            'Green energy targets',
            'ESG compliance'
          ]
        }
      ]
    },
    hi: {
      title: 'उपयोगिता प्रबंधन और स्मार्ट ग्रिड',
      subtitle: 'AI-संचालित निगरानी के साथ ऊर्जा वितरण को अनुकूलित करें',
      tabs: [
        {
          name: 'स्मार्ट ग्रिड',
          icon: 'BoltIcon',
          features: [
            'वास्तविक समय ग्रिड बैलेंसिंग',
            'मांग प्रतिक्रिया अनुकूलन',
            'नवीकरणीय एकीकरण',
            'पीक लोड प्रबंधन'
          ]
        },
        {
          name: 'ऊर्जा निगरानी',
          icon: 'ChartBarIcon',
          features: [
            'IoT सेंसर नेटवर्क',
            'भविष्य कहनेवाला विश्लेषण',
            'खपत पूर्वानुमान',
            'विसंगति का पता लगाना'
          ]
        },
        {
          name: 'स्थिरता',
          icon: 'SparklesIcon',
          features: [
            'कार्बन ट्रैकिंग',
            'उत्सर्जन रिपोर्टिंग',
            'हरित ऊर्जा लक्ष्य',
            'ESG अनुपालन'
          ]
        }
      ]
    },
    ar: {
      title: 'إدارة المرافق والشبكة الذكية',
      subtitle: 'تحسين توزيع الطاقة بمراقبة مدعومة بالذكاء الاصطناعي',
      tabs: [
        {
          name: 'الشبكة الذكية',
          icon: 'BoltIcon',
          features: [
            'موازنة الشبكة في الوقت الفعلي',
            'تحسين استجابة الطلب',
            'دمج الطاقة المتجددة',
            'إدارة الحمل الذروي'
          ]
        },
        {
          name: 'مراقبة الطاقة',
          icon: 'ChartBarIcon',
          features: [
            'شبكات أجهزة استشعار IoT',
            'التحليلات التنبؤية',
            'توقعات الاستهلاك',
            'كشف الشذوذ'
          ]
        },
        {
          name: 'الاستدامة',
          icon: 'SparklesIcon',
          features: [
            'تتبع الكربون',
            'تقارير الانبعاثات',
            'أهداف الطاقة الخضراء',
            'الامتثال ESG'
          ]
        }
      ]
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="py-16 md:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {currentContent.tabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`flex-1 px-6 py-4 rounded-lg font-semibold transition-all duration-300 ${
                activeTab === index
                  ? 'bg-primary text-primary-foreground shadow-natural'
                  : 'bg-card text-text-secondary hover:bg-card/80 border border-border'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Icon name={tab.icon as any} size={20} />
                <span>{tab.name}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-card border border-border rounded-xl p-8 shadow-natural">
          <div className="grid md:grid-cols-2 gap-6">
            {currentContent.tabs[activeTab].features.map((feature, index) => (
              <div 
                key={index}
                className="flex items-start space-x-3 p-4 bg-background rounded-lg border border-border hover:border-primary/50 transition-colors"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="CheckCircleIcon" size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary mb-1">{feature}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UtilitiesSection;